import { useEffect, useState } from "react";
import { Box, Alert, LinearProgress, Grid, Typography } from "@mui/material";

import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { RootState, store } from "@store/store";
import { useDispatch, useSelector } from "react-redux";

import { DialogType } from "@interfaces/slices";
import { ResultadosPld } from "@features/Solicitud/subFeatures/PldIntegral/components/ResultadosPld";
import { DeclaratoriaPld } from "@features/Solicitud/subFeatures/PldIntegral/components/DeclaratoriaPld";
import { EstatusAlertasPrePld } from "@features/Solicitud/subFeatures/PldIntegral/components/EstatusAlertasPrePld";
import { setDialogConfig, setPrintingReset } from "@store/slices/componentsSlice";
import {
    useLazyGetDocumentsQuery,
    useLazyGetSignUrlToUploadDocQuery,
    usePatchDocumentMutation,
    usePostDocumentMutation,
    usePostUploadFileMutation,
} from "@services/api/documents";
import { useLazyGetFileCategoriesQuery } from "@services/api/catalogs";

export const DescargarReportePld = () => {
    const dispatch = useDispatch();

    const [progress, setProgress] = useState<number>(0);

    // const applicationSelected = (state = store.getState()) => state.components.applicationSelected;

    const userSession = useSelector((state: RootState) => state.session.user);
    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);
    const printingProfileReport = useSelector((state: RootState) => state.components.printingProfileReport);

    const [triggerGetDocs] = useLazyGetDocumentsQuery();
    const [triggerPostDocument] = usePostDocumentMutation();
    const [triggerPatchDocument] = usePatchDocumentMutation();
    const [triggerPostUploadDoc] = usePostUploadFileMutation();
    const [triggerGetDocsCategory] = useLazyGetFileCategoriesQuery();
    const [triggetPostUrlToUploadDoc] = useLazyGetSignUrlToUploadDocQuery();

    const saveDoc = async (doc: Blob) => {
        try {
            const categories = await triggerGetDocsCategory("").unwrap();
            const pldCategory = categories.find((category) => category.code === "DPLD");

            if (pldCategory === undefined) return;

            const urlToUploadResponse = await triggetPostUrlToUploadDoc({
                upload_type: "client",
                file_name: "Reporte_PLD.pdf",
                regimen: applicationSelected.tax_system.tax_system_type.toLowerCase(),
                person_id: undefined,
                person_type: undefined,
                client_details_id: applicationSelected.client_detail_id,
                file_category_id: pldCategory.id,
                loan_application_id: applicationSelected.id,
            }).unwrap();

            const signUrl = urlToUploadResponse.data;

            const tmp = new FormData();

            tmp.append("key", signUrl.fields.key);
            tmp.append("AWSAccessKeyId", signUrl.fields.AWSAccessKeyId);
            tmp.append("x-amz-security-token", signUrl.fields["x-amz-security-token"]);
            tmp.append("policy", signUrl.fields.policy);
            tmp.append("signature", signUrl.fields.signature);
            tmp.append("file", doc);

            await triggerPostUploadDoc({
                url: signUrl.url,
                body: tmp,
            }).unwrap();

            const currentDocs = await triggerGetDocs({
                loan_application_id: applicationSelected.id,
                client_details_id: applicationSelected.client_detail_id,
            }).unwrap();

            const currentPld = currentDocs.find((doc) => doc.file_code === "DPLD");

            if (currentPld !== undefined) {
                if (currentPld.validated) {
                    await triggerPatchDocument({
                        applicationId: applicationSelected.id,
                        documentId: currentPld.id ?? "0-0-0-0-0",
                        body: {
                            validated: false,
                            user_name:
                                userSession.name
                                    ?.concat(" ", userSession?.last_name ?? "", " ", userSession?.last_name_2 ?? "")
                                    .trim() ?? "",
                        },
                    }).unwrap();
                }
                await triggerPatchDocument({
                    applicationId: applicationSelected.id,
                    documentId: currentPld.id ?? "0-0-0-0-0",
                    body: {
                        status: false,
                        user_name:
                            userSession.name
                                ?.concat(" ", userSession?.last_name ?? "", " ", userSession?.last_name_2 ?? "")
                                .trim() ?? "",
                    },
                }).unwrap();
            }

            await triggerPostDocument({
                loan_application_id: applicationSelected.id ?? "0-0-0-0-0",
                file_category_id: pldCategory.id,
                url: signUrl.fields.key,
                file_name: "Reporte_PLD.pdf",
                client_details_id: applicationSelected.client_detail_id,
                person_id: undefined,
                user_name:
                    userSession.name
                        ?.concat(" ", userSession?.last_name ?? "", " ", userSession?.last_name_2 ?? "")
                        .trim() ?? "",
            }).unwrap();
        } catch (error) {
            console.log(error);
        }
    };

    const handleOnClick = async () => {
        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "A4",
        });

        const element = document.getElementById("temp");
        if (!element) return;

        const canvas = await html2canvas(element, { scale: 2 });
        const imgData = canvas.toDataURL("image/jpeg");

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        const ratio = imgProps.width / pdfWidth;

        const cropWidth = imgProps.width;
        const cropHeight = 4500;

        let count = 0;
        let cropY = 0;
        while (cropY < imgProps.height) {
            setProgress(Math.round((cropY * 100) / imgProps.height) + 20);
            const canvasPage = document.createElement("canvas");
            canvasPage.width = imgProps.width;
            canvasPage.height = pdfHeight * ratio;
            const ctx = canvasPage.getContext("2d");
            if (ctx === null) continue;

            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(canvas, 0, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

            const tmp = canvasPage.toDataURL("image/jpeg");
            pdf.addImage(tmp, "JPEG", 10, 10, pdfWidth - 20, pdfHeight - 50);
            pdf.addPage();

            count += 1;
            cropY += cropHeight;
        }

        pdf.deletePage(count + 1);

        //TODO Guardar reporte como documento de la empresa.
        const pdfBlob = pdf.output("blob");

        await saveDoc(pdfBlob);
        await pdf.save(`ReportePld_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`, { returnPromise: true });

        dispatch(setPrintingReset());
        dispatch(setDialogConfig());
    };

    useEffect(() => {
        if (
            printingProfileReport.module.resultsPldReady &&
            printingProfileReport.module.statementsPldReady &&
            printingProfileReport.module.statusAlertsPldReady
        ) {
            setProgress(20);
            setTimeout(async () => {
                await handleOnClick();
            }, 3000);
        }
    }, [printingProfileReport]);

    return (
        <>
            <Box sx={{
                minWidth: 100
            }}>{/* <Alert severity="info">Generando reporte PLD... {progress}%</Alert> */}</Box>
            <Box>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 10 }} />
            </Box>
            <Box sx={{
                overflow: "scroll"
            }}>
                <Grid
                    container
                    id="temp"
                    sx={{
                        pt: 1,
                        pl: 3
                    }}>
                    <Grid container size={12} direction={"column"}>
                        <Typography
                            variant="h4"
                            sx={{
                                color: "#002652",
                                fontWeight: 600,
                                mt: 3,
                                mb: 3
                            }}>
                            Alertas de PLD
                        </Typography>

                        <EstatusAlertasPrePld />

                        <Typography
                            variant="h4"
                            sx={{
                                color: "#002652",
                                fontWeight: 600,
                                mt: 3,
                                mb: 3
                            }}>
                            Declaratoria de PLD
                        </Typography>
                        <DeclaratoriaPld />

                        <Typography
                            variant="h4"
                            sx={{
                                color: "#002652",
                                fontWeight: 600,
                                mt: 3,
                                mb: 3
                            }}>
                            Detalle Consulta PLD
                        </Typography>
                        <ResultadosPld />
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};
